// Push Notification Service for frontend

const API_BASE_URL = '/api';

class PushNotificationService {
  constructor() {
    this.vapidPublicKey = null;
    this.subscription = null;
  }

  /**
   * Check if push notifications are supported in this browser
   */
  isPushSupported() {
    return 'PushManager' in window && 'Notification' in window && 'serviceWorker' in navigator;
  }

  /**
   * Get current notification permission state
   */
  getPermissionState() {
    if (!this.isPushSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Check if user is already subscribed
   */
  async isSubscribed() {
    if (!this.isPushSupported()) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return subscription !== null;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Get VAPID public key from backend
   */
  async getVapidPublicKey() {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/push/vapidPublicKey`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to get VAPID public key: ${response.status}`);
      }

      const data = await response.json();
      this.vapidPublicKey = data.publicKey;
      return this.vapidPublicKey;
    } catch (error) {
      console.error('Error fetching VAPID public key:', error);
      throw error;
    }
  }

  /**
   * Convert base64 string to Uint8Array (for VAPID key)
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Request notification permission and subscribe to push
   */
  async subscribeToPush() {
    console.log('[PushService] Starting subscription process...');
    
    if (!this.isPushSupported()) {
      console.error('[PushService] Push not supported');
      throw new Error('Push notifications are not supported in this browser');
    }

    try {
      // Request notification permission
      console.log('[PushService] Requesting notification permission...');
      const permission = await Notification.requestPermission();
      console.log('[PushService] Permission result:', permission);
      
      if (permission !== 'granted') {
        throw new Error('Notification permission denied');
      }

      // Get service worker registration
      console.log('[PushService] Waiting for service worker to be ready...');
      const registration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Service Worker timeout')), 10000))
      ]);
      console.log('[PushService] Service worker ready:', registration);

      // Check if already subscribed
      console.log('[PushService] Checking existing subscription...');
      let subscription = await registration.pushManager.getSubscription();
      console.log('[PushService] Existing subscription:', subscription ? 'Found' : 'None');

      if (!subscription) {
        // Get VAPID public key
        console.log('[PushService] Fetching VAPID public key...');
        const vapidPublicKey = await this.getVapidPublicKey();
        console.log('[PushService] VAPID key received:', vapidPublicKey.substring(0, 20) + '...');
        
        const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);
        console.log('[PushService] Converted to Uint8Array');

        // Subscribe to push notifications
        console.log('[PushService] Subscribing to push manager...');
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
        console.log('[PushService] Push manager subscription successful');
      }

      // Send subscription to backend
      console.log('[PushService] Sending subscription to backend...');
      await this.sendSubscriptionToBackend(subscription);
      console.log('[PushService] Backend subscription successful');

      this.subscription = subscription;

      // Store success state
      localStorage.setItem('push-notifications-enabled', 'true');
      localStorage.setItem('push-last-subscribed', new Date().toISOString());

      console.log('[PushService] Subscription process completed successfully');
      return { success: true, subscription };
    } catch (error) {
      console.error('[PushService] Error subscribing to push notifications:', error);
      throw error;
    }
  }

  /**
   * Send subscription details to backend
   */
  async sendSubscriptionToBackend(subscription) {
    console.log('[PushService] sendSubscriptionToBackend called');
    const subscriptionJson = subscription.toJSON();
    console.log('[PushService] Subscription JSON:', subscriptionJson);

    const payload = {
      endpoint: subscriptionJson.endpoint,
      p256dhKey: subscriptionJson.keys.p256dh,
      authKey: subscriptionJson.keys.auth,
      userAgent: navigator.userAgent
    };
    console.log('[PushService] Payload prepared:', { ...payload, endpoint: payload.endpoint.substring(0, 50) + '...' });

    try {
      console.log('[PushService] Sending POST to', `${API_BASE_URL}/push/subscribe`);
      const response = await fetch(`${API_BASE_URL}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      console.log('[PushService] Backend response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[PushService] Backend error response:', errorText);
        throw new Error(`Failed to save subscription: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('[PushService] Backend response:', result);
      return result;
    } catch (error) {
      console.error('[PushService] Error sending subscription to backend:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribeFromPush() {
    if (!this.isPushSupported()) {
      return { success: false, error: 'Not supported' };
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from push manager
        await subscription.unsubscribe();

        // Notify backend
        const subscriptionJson = subscription.toJSON();
        await fetch(`${API_BASE_URL}/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ endpoint: subscriptionJson.endpoint })
        });
      }

      this.subscription = null;
      localStorage.removeItem('push-notifications-enabled');

      return { success: true };
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      throw error;
    }
  }

  /**
   * Check if prompt was dismissed recently
   */
  wasPromptDismissedRecently() {
    const dismissedAt = localStorage.getItem('push-prompt-dismissed');
    if (!dismissedAt) {
      return false;
    }

    const daysSinceDismissal = (Date.now() - new Date(dismissedAt).getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceDismissal < 7; // Don't show again for 7 days
  }

  /**
   * Mark prompt as dismissed
   */
  markPromptAsDismissed() {
    localStorage.setItem('push-prompt-dismissed', new Date().toISOString());
  }

  /**
   * Detect iOS platform
   */
  isIOS() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  /**
   * Check if running as installed PWA
   */
  isInstalled() {
    // iOS
    if (this.isIOS() && window.navigator.standalone) {
      return true;
    }
    // Android/Desktop
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return true;
    }
    return false;
  }

  /**
   * Send test notification (for debugging)
   */
  async sendTestNotification() {
    try {
      console.log('[PushService] Sending test notification request...');
      const response = await fetch(`${API_BASE_URL}/push/test`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('[PushService] Test notification response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[PushService] Test notification failed:', errorText);
        throw new Error(`Failed to send test notification: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('[PushService] Test notification result:', result);
      return result;
    } catch (error) {
      console.error('Error sending test notification:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new PushNotificationService();
